import { Box, Button, ButtonBase, Link, Skeleton, Typography, useTheme } from "@mui/material";

import { PatientHexAvatar } from "../../../../components/ui";
import { relativeDayLabel } from "../../../../utils/format-date";
import { RecentConsultation } from "../use-dashboard-data";

// ----------------------------------------------------------------------

interface RecentConsultationsProps {
  items: RecentConsultation[];
  loading: boolean;
  onOpen: (item: RecentConsultation) => void;
  onViewAll: () => void;
  onStart: () => void;
}

export default function RecentConsultations({ items, loading, onOpen, onViewAll, onStart }: RecentConsultationsProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "18px",
        p: "26px 28px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "20px" }}>
        <Typography
          component="h3"
          sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: 23, color: "text.primary" }}
        >
          Últimas consultas
        </Typography>
        <Link component="button" underline="none" onClick={onViewAll} sx={{ fontSize: "13px", fontWeight: 600, color: "primary.main" }}>
          Ver todas
        </Link>
      </Box>

      {loading &&
        [0, 1, 2].map((i) => <Skeleton key={i} height={56} sx={{ borderRadius: "12px" }} />)}

      {!loading && items.length === 0 && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
            Nenhuma consulta ainda — comece agora.
          </Typography>
          <Button variant="contained" onClick={onStart} sx={{ mt: 2 }}>
            Iniciar consulta
          </Button>
        </Box>
      )}

      {!loading &&
        items.map((item, index) => (
          <Box key={`${item.type}-${item.id}`}>
            {index > 0 && <Box sx={{ height: "1px", backgroundColor: theme.palette.divider }} />}
            <ButtonBase
              onClick={() => onOpen(item)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                gap: "14px",
                p: "13px 10px",
                borderRadius: "12px",
                textAlign: "left",
                "&:hover": { backgroundColor: theme.palette.brand.surfaceHover },
              }}
            >
              <PatientHexAvatar name={item.patientName} size={40} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>
                  {item.patientName}
                </Typography>
                {item.focus && (
                  <Typography noWrap sx={{ fontSize: "12px", color: "text.secondary" }}>
                    {item.focus}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: "12.5px", color: "text.secondary", flexShrink: 0 }}>
                {relativeDayLabel(item.date)}
              </Typography>
            </ButtonBase>
          </Box>
        ))}
    </Box>
  );
}
